import { create } from 'zustand';
import type {
  ModelShare,
  OverviewTotals,
  ProviderHealth,
  RangeKey,
  RealmShare,
  ReconciliationDay,
  SeriesPoint,
  TenantUsageTotal,
} from '../data/types';
import { getSources } from './sources';
import type { AnalyticsQuery, ModelSeries, ThroughputSeries } from './sources/types';

/* Charts come from BigQuery through the control plane, which means seconds, not
 * milliseconds. Two consequences shape this store:
 *
 *   The resolved series stays mounted while the next query runs. `loading` is a
 *   flag beside the data, never a replacement for it — a chart that unmounts
 *   and remounts on every range change is the flash this console does not do.
 *
 *   A generation guard drops the answer to a range the operator has already
 *   moved off. Without it, an 8-second query for "30 days" would overwrite the
 *   "24 hours" chart the operator switched to four seconds ago. */

export type Panel =
  | 'totals'
  | 'throughput'
  | 'models'
  | 'tenants'
  | 'providers'
  | 'realms'
  | 'recon';

export interface AnalyticsState {
  range: RangeKey;
  totals?: OverviewTotals;
  throughput?: ThroughputSeries;
  models?: ModelSeries;
  tenants?: TenantUsageTotal[];
  providers?: ProviderHealth[];
  realms?: RealmShare[];
  recon?: ReconciliationDay[];

  loading: Partial<Record<Panel, boolean>>;
  errors: Partial<Record<Panel, string>>;
  /** No panel has ever been asked for. Distinct from "asked and got nothing". */
  wired: boolean;

  setRange(range: RangeKey): void;
  load(q: AnalyticsQuery): void;
  reset(): void;
  hydrate(patch: Partial<AnalyticsState>): void;
}

const initial = {
  range: '30 days' as RangeKey,
  loading: {},
  errors: {},
  wired: false,
};

/** What a panel should render right now.
 *
 *  `pending` means nothing has EVER arrived for it, so a skeleton stands in.
 *  Once a panel holds data it keeps it: a re-query — a range change, a
 *  re-scope — leaves the resolved series on screen and swaps it when the next
 *  one lands. A chart that empties itself for the two seconds BigQuery takes is
 *  the flash this console does not do.
 *
 *  A resolved EMPTY array is `empty`, not `pending`: "no tenant billed
 *  anything" is an answer, and showing a skeleton forever because the answer
 *  was zero rows would be a spinner that never stops. */
export type PanelPhase = 'data' | 'pending' | 'empty';

export function panelPhase(data: unknown, error?: string): PanelPhase {
  if (data !== undefined) {
    // An empty array has answered. Anything else with a value is data.
    return Array.isArray(data) && data.length === 0 ? 'empty' : 'data';
  }
  // An error is not pending — it has finished, badly, and the panel says so
  // rather than pretending it is still working.
  return error ? 'empty' : 'pending';
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => {
  // One generation for the whole panel set: every panel on a screen answers the
  // same (range, scope), so they invalidate together.
  let gen = 0;

  const run = <K extends Panel, V>(
    my: number,
    panel: K,
    p: Promise<V>,
    assign: (v: V) => Partial<AnalyticsState>,
  ) => {
    set((s) => ({ loading: { ...s.loading, [panel]: true } }));
    p.then(
      (v) => {
        if (my !== gen) return;
        set((s) => ({
          ...assign(v),
          loading: { ...s.loading, [panel]: false },
          errors: { ...s.errors, [panel]: undefined },
        }));
      },
      (err: unknown) => {
        if (my !== gen) return;
        set((s) => ({
          loading: { ...s.loading, [panel]: false },
          errors: {
            ...s.errors,
            [panel]: err instanceof Error ? err.message : String(err),
          },
        }));
      },
    );
  };

  return {
    ...initial,

    setRange(range) {
      if (get().range === range) return;
      set({ range });
    },

    load(q) {
      gen += 1;
      const my = gen;
      set({ wired: true });
      const src = getSources().query;
      run(my, 'totals', src.totals(q), (v) => ({ totals: v }));
      run(my, 'throughput', src.throughput(q), (v) => ({ throughput: v }));
      run(my, 'models', src.tokensByModel(q), (v) => ({ models: v }));
      run(my, 'tenants', src.usageByTenant(q), (v) => ({ tenants: v }));
      run(my, 'providers', src.providerHealth(q), (v) => ({ providers: v }));
      run(my, 'realms', src.realmMix(q), (v) => ({ realms: v }));
      run(my, 'recon', src.reconciliation(q), (v) => ({ recon: v }));
    },

    reset() {
      gen += 1;
      set({
        ...initial,
        range: get().range,
        totals: undefined,
        throughput: undefined,
        models: undefined,
        tenants: undefined,
        providers: undefined,
        realms: undefined,
        recon: undefined,
      });
    },

    hydrate(patch) {
      gen += 1;
      set({ ...patch, wired: true, loading: {}, errors: {} });
    },
  };
});

export type { SeriesPoint, ModelShare };
